/**
 * OQ-333 · Khối "Dọn nhật ký cũ" trong Cài đặt → Sao lưu.
 *
 * **Chủ dự án**: *"nhật ký hoạt động đang ghi và lưu trữ sự kiện trong bao lâu,
 * khi lượng người dùng tăng cao thì có sợ dữ liệu quá nhiều không, có cần phải
 * làm sạch định kỳ hay không?"* — chọn: **xuất ra tệp rồi xoá, bấm tay**.
 *
 * ── Vì sao đặt ở tab Sao lưu, không phải tab riêng ─────────────────────
 *
 * Việc này sinh ra tệp và nằm cùng thư mục với bản sao lưu CSDL. Người đi tìm
 * "các tệp hệ thống đã tạo ra" sẽ tìm ở một chỗ, không phải hai.
 *
 * ── Nói TRƯỚC số dòng sắp mất ──────────────────────────────────────────
 *
 * Nút này xoá dữ liệu không lấy lại được từ trong ứng dụng. Một hộp thoại hỏi
 * "bạn có chắc không" mà không kèm con số là hỏi cho có — người dùng không có gì
 * để mà chắc. Nên trước khi hiện nút xoá, gọi máy chủ đếm và hiện đúng con số ấy.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { useFormat } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface TepNhatKy {
  name: string;
  soDong: number;
  bytes: number;
  createdAt: string;
}

/** Mặc định 365 ngày — đúng mức tối thiểu `docs/02` FR-135 đòi giữ lại. */
const MAC_DINH_NGAY = 365;

function mocTruoc(soNgay: number): string {
  return new Date(Date.now() - soNgay * 86_400_000).toISOString().slice(0, 10);
}

export function AuditArchivePanel(): JSX.Element {
  const t = useT();
  const { formatDateTime, formatNumber } = useFormat();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const [truocNgay, setTruocNgay] = useState(mocTruoc(MAC_DINH_NGAY));
  const [hoiChac, setHoiChac] = useState(false);

  const loiGon = (err: unknown): string =>
    err instanceof ApiError ? err.moTaRoRang : t('state.error.title');

  const chiMuc = useQuery({
    queryKey: ['audit-logs', 'search-index'],
    queryFn: () =>
      api.get<{ coExtension: boolean; soChiMuc: number; dayDu: boolean }>(
        '/audit-logs/search-index',
      ),
  });

  const ds = useQuery({
    queryKey: ['settings', 'audit-archives'],
    queryFn: () => api.get<{ items: TepNhatKy[] }>('/settings/audit-archives'),
  });

  const xemTruoc = useQuery({
    queryKey: ['settings', 'audit-archives', 'preview', truocNgay],
    queryFn: () =>
      api.get<{ soDong: number; giuToiThieuNgay: number }>(
        `/settings/audit-archives/preview?truocNgay=${encodeURIComponent(truocNgay)}`,
      ),
    enabled: truocNgay !== '',
  });

  const duaRa = useMutation({
    mutationFn: () =>
      api.post<{ name: string; soDong: number; daXoa: number }>('/settings/audit-archives', {
        truocNgay,
      }),
    onSuccess: async (kq) => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'audit-archives'] });
      setHoiChac(false);
      toast(t('settings.auditArchive.done', { soDong: kq.soDong, name: kq.name }));
    },
    onError: (err) => {
      setHoiChac(false);
      toastError(loiGon(err));
    },
  });

  const xoaTep = useMutation({
    mutationFn: (name: string) =>
      api.del<void>(`/settings/audit-archives/${encodeURIComponent(name)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'audit-archives'] });
      toast(t('settings.auditArchive.fileRemoved'));
    },
    onError: (err) => toastError(loiGon(err)),
  });

  const items = ds.data?.items ?? [];
  const soDong = xemTruoc.data?.soDong ?? 0;

  return (
    <div className="card" data-od-id="audit-archive-card">
      <div className="card-head">
        <div className="grow">
          <h2>{t('settings.auditArchive.title')}</h2>
          <div className="sub">{t('settings.auditArchive.sub')}</div>
        </div>
      </div>

      <div className="card-body stack">
        <div className="form-alert" role="note">
          <Icon name="info" size={16} />
          <span>{t('settings.auditArchive.lede')}</span>
        </div>

        {/*
          OQ-334 — nói thẳng chỉ mục tìm kiếm đang bật hay không.

          Migration `0026` bọc lệnh tạo chỉ mục trong khối bắt lỗi, vì `pg_trgm`
          không có trên mọi bản PostgreSQL. Một khối nuốt lỗi là một chỗ có thể
          hỏng mà không ai biết — dòng này để người vận hành tự kiểm, thay vì phải
          tin rằng nó đã chạy.

          Chỉ hiện khi THIẾU: lúc đủ thì đây là thông tin không ai cần đọc, và một
          dòng xanh "mọi thứ ổn" lặp lại mỗi lần mở trang chỉ dạy người ta bỏ qua
          cả khu vực ấy.
        */}
        {chiMuc.data && !chiMuc.data.dayDu ? (
          <div className="form-alert warn" role="note" data-od-id="audit-search-index-warn">
            <Icon name="warn" size={16} />
            <span>{t('settings.auditArchive.noIndex')}</span>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="audit-truoc-ngay">{t('settings.auditArchive.before')}</label>
          <input
            id="audit-truoc-ngay"
            type="date"
            value={truocNgay}
            max={mocTruoc(MAC_DINH_NGAY)}
            onChange={(e) => setTruocNgay(e.target.value)}
            data-od-id="audit-archive-date"
          />
          <span className="hint">{t('settings.auditArchive.beforeHint')}</span>
        </div>

        <p className={soDong > 0 ? undefined : 'muted'}>
          {t('settings.auditArchive.count', { soDong: formatNumber(soDong) })}
        </p>

        <div className="row">
          <button
            type="button"
            className="btn btn-primary"
            disabled={soDong === 0 || duaRa.isPending}
            onClick={() => setHoiChac(true)}
            data-od-id="audit-archive-run"
          >
            {duaRa.isPending ? t('common.processing') : t('settings.auditArchive.run')}
          </button>
        </div>

        {items.length > 0 ? (
          <div className="table-wrap">
            <table className="tbl" data-od-id="audit-archive-list">
              <thead>
                <tr>
                  <th>{t('settings.auditArchive.col.file')}</th>
                  <th>{t('settings.auditArchive.col.lines')}</th>
                  <th>{t('settings.auditArchive.col.size')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((f) => (
                  <tr key={f.name}>
                    <td>
                      <span className="mono small">{f.name}</span>
                      <div className="sub">{formatDateTime(f.createdAt)}</div>
                    </td>
                    <td>{formatNumber(f.soDong)}</td>
                    <td>{(f.bytes / 1024).toFixed(0)} KB</td>
                    <td>
                      <div className="row-actions">
                        <a
                          className="btn btn-sm btn-ghost"
                          href={`/api/v1/settings/audit-archives/${encodeURIComponent(f.name)}/download`}
                        >
                          {t('settings.auditArchive.download')}
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          disabled={xoaTep.isPending}
                          onClick={() => xoaTep.mutate(f.name)}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {hoiChac ? (
        <ConfirmDialog
          open
          title={t('settings.auditArchive.confirmTitle')}
          message={t('settings.auditArchive.confirmBody', {
            soDong: formatNumber(soDong),
            ngay: truocNgay,
          })}
          confirmLabel={t('settings.auditArchive.run')}
          danger
          busy={duaRa.isPending}
          odId="audit-archive-confirm"
          onCancel={() => setHoiChac(false)}
          onConfirm={() => duaRa.mutate()}
        />
      ) : null}
    </div>
  );
}
